import { migratePersistedSearchState } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchMode'
import type { DocumentTab } from 'features/knowledgeDocumentViewer/types'
import {
  getDocChatStorageOwner,
  isCurrentDocChatStorageOwner,
} from 'features/knowledgeDocumentViewer/utils/docChatStorageOwner'
import { SelectedTab } from 'features/navbar/types'
import { z } from 'zod'

const TAB_STATE_PREFIX = 'gecko_docpage_tabs'
const CHATBOT_PREFIX = 'gecko_docpage_chatbot'

const CURRENT_VERSION = 2

const selectedTabSchema = z.enum(SelectedTab)

/** Persistence-safe history item: require discriminator + loose payload. */
const tabHistoryItemSchema = z.union([
  z.looseObject({
    type: z.literal('search'),
    searchState: z.record(z.string(), z.unknown()),
  }),
  z.looseObject({
    type: z.literal('document'),
    documentId: z.string(),
    pageNumber: z.number(),
    selectedBoxIds: z.array(z.string()),
    query: z.string(),
  }),
  z.looseObject({
    type: z.literal('destination'),
    // Settings and other non-SelectedTab destinations use free-form keys.
    destinationKey: z.string().min(1),
    label: z.string(),
    href: z.string(),
  }),
  z.looseObject({
    type: z.literal('artifact'),
    artifactId: z.string().min(1),
  }),
])

const documentTabShapeSchema = z.object({
  id: z.string().min(1),
  history: z.array(tabHistoryItemSchema).min(1),
  currentIndex: z.number().int().nonnegative(),
  customLabel: z.string().optional(),
  workspaceDestination: selectedTabSchema.optional(),
  closable: z.boolean().optional(),
  resourceKey: z.string().optional(),
  openedAt: z.number().optional(),
})

/** Validated at the sessionStorage boundary; typed as DocumentTab for callers. */
const documentTabSchema = z.custom<DocumentTab>(
  (value) => documentTabShapeSchema.safeParse(value).success
)

const persistedTabStateSchema = z.object({
  _v: z.union([z.literal(1), z.literal(CURRENT_VERSION)]),
  openTabs: z.array(documentTabSchema).min(1),
  activeTabId: z.string().min(1),
})

export interface PersistedTabState {
  _v: number
  openTabs: DocumentTab[]
  activeTabId: string
}

interface PersistedChatbotState {
  _v: number
  isChatbotOpen: boolean
}

/** Writes to sessionStorage only when the value has changed. */
function setIfChanged(key: string, serialized: string): void {
  if (sessionStorage.getItem(key) === serialized) return
  sessionStorage.setItem(key, serialized)
}

function handleQuotaError(e: unknown, key: string): void {
  if (e instanceof DOMException && e.name === 'QuotaExceededError') {
    try {
      sessionStorage.removeItem(key)
    } catch {
      /* noop */
    }
  }
}

function migratePersistedTab(tab: DocumentTab): DocumentTab {
  return {
    ...tab,
    history: tab.history.map((item) => {
      if (item.type !== 'search') return item
      return {
        ...item,
        searchState: migratePersistedSearchState(item.searchState),
      }
    }),
  }
}

function sanitizePersistedTabs(
  parsed: z.infer<typeof persistedTabStateSchema>
): PersistedTabState | null {
  const openTabs = parsed.openTabs
    .filter(
      (tab) => tab.currentIndex >= 0 && tab.currentIndex < tab.history.length
    )
    .map(migratePersistedTab)
  if (openTabs.length === 0) return null

  const activeTabId = openTabs.some((tab) => tab.id === parsed.activeTabId)
    ? parsed.activeTabId
    : openTabs[0].id

  return {
    _v: CURRENT_VERSION,
    openTabs,
    activeTabId,
  }
}

function tabStateKey(owner: string, siteId: string): string {
  return `${TAB_STATE_PREFIX}_${owner}_${siteId}`
}

export function getPersistedTabState(siteId: string): PersistedTabState | null {
  const owner = getDocChatStorageOwner()
  if (!owner) return null
  const key = tabStateKey(owner, siteId)
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    const result = persistedTabStateSchema.safeParse(JSON.parse(raw))
    if (!result.success) return null
    return sanitizePersistedTabs(result.data)
  } catch {
    return null
  }
}

export function persistTabState(
  owner: string | null,
  siteId: string,
  openTabs: PersistedTabState['openTabs'],
  activeTabId: string
): void {
  if (!isCurrentDocChatStorageOwner(owner)) return
  const key = tabStateKey(owner, siteId)
  try {
    const data: PersistedTabState = {
      _v: CURRENT_VERSION,
      openTabs,
      activeTabId,
    }
    setIfChanged(key, JSON.stringify(data))
  } catch (e) {
    handleQuotaError(e, key)
  }
}

export function getPersistedChatbotState(
  siteId: string
): PersistedChatbotState | null {
  try {
    const raw = sessionStorage.getItem(`${CHATBOT_PREFIX}_${siteId}`)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      !('_v' in parsed) ||
      !('isChatbotOpen' in parsed)
    ) {
      return null
    }
    const version = parsed._v
    const isChatbotOpen = parsed.isChatbotOpen
    if (version !== 1 && version !== CURRENT_VERSION) return null
    if (typeof isChatbotOpen !== 'boolean') return null
    return { _v: version, isChatbotOpen }
  } catch {
    return null
  }
}

export function persistChatbotState(
  siteId: string,
  isChatbotOpen: boolean
): void {
  try {
    const key = `${CHATBOT_PREFIX}_${siteId}`
    const data: PersistedChatbotState = { _v: CURRENT_VERSION, isChatbotOpen }
    setIfChanged(key, JSON.stringify(data))
  } catch (e) {
    handleQuotaError(e, `${CHATBOT_PREFIX}_${siteId}`)
  }
}
